import { create } from 'zustand';
import { userService } from '../lib/api/users';

export enum OnboardingStep {
  WELCOME = 'welcome',
  CREATE_BOARD = 'create_board',
  ADD_DATA = 'add_data',
  SET_DATE_RANGE = 'set_date_range',
  ADD_SPEC = 'add_spec',
  COMPLETED = 'completed',
}

interface OnboardingStatus {
  currentStep: OnboardingStep;
  isDismissed: boolean;
  completedSteps: OnboardingStep[];
}

export interface OnboardingState {
  status: OnboardingStatus;
  isActive: boolean;
  isInitialized: boolean;
  suppressedStep: OnboardingStep | null;
  // Actions
  init: (statusUpdate?: string | Partial<OnboardingStatus>) => void;
  setStep: (step: OnboardingStep) => Promise<void>;
  completeStep: (step: OnboardingStep) => Promise<void>;
  dismiss: () => Promise<void>;
  reset: () => Promise<void>;
  clear: () => void;
  start: () => void;
  suppressStep: (step: OnboardingStep) => void;
}

const DEFAULT_STATUS: OnboardingStatus = {
  currentStep: OnboardingStep.WELCOME,
  isDismissed: false,
  completedSteps: [],
};

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  status: DEFAULT_STATUS,
  isActive: false,
  isInitialized: false,
  suppressedStep: null,

  init: (statusUpdate) => {
    let status = { ...DEFAULT_STATUS };
    
    if (typeof statusUpdate === 'string') {
      try {
        const parsed = JSON.parse(statusUpdate);
        status = { ...DEFAULT_STATUS, ...parsed };
      } catch (e) {
        console.error('Failed to parse onboarding status', e);
      }
    } else if (statusUpdate) {
      status = { ...status, ...statusUpdate };
    }

    set({ 
      status, 
      isActive: !status.isDismissed && status.currentStep !== OnboardingStep.COMPLETED, 
      isInitialized: true,
      suppressedStep: null 
    });
  },

  setStep: async (step: OnboardingStep) => {
    const newStatus = { ...get().status, currentStep: step };
    set({ status: newStatus, suppressedStep: null });
    
    try {
      await userService.updateProfile({ onboardingStatus: JSON.stringify(newStatus) });
    } catch (e) {
      console.error('Failed to sync onboarding status', e);
    }
  },

  completeStep: async (step: OnboardingStep) => {
    const { status } = get();
    if (status.completedSteps.includes(step)) return;

    const newCompleted = [...status.completedSteps, step];
    
    // Define sequence logic
    let nextStep = status.currentStep;
    if (step === OnboardingStep.WELCOME) nextStep = OnboardingStep.CREATE_BOARD;
    else if (step === OnboardingStep.CREATE_BOARD) nextStep = OnboardingStep.ADD_DATA;
    else if (step === OnboardingStep.ADD_DATA) nextStep = OnboardingStep.SET_DATE_RANGE;
    else if (step === OnboardingStep.SET_DATE_RANGE) nextStep = OnboardingStep.ADD_SPEC;
    else if (step === OnboardingStep.ADD_SPEC) nextStep = OnboardingStep.COMPLETED;

    const newStatus = { 
      ...status, 
      completedSteps: newCompleted,
      currentStep: nextStep 
    };

    set({ 
      status: newStatus,
      isActive: nextStep !== OnboardingStep.COMPLETED,
      suppressedStep: null
    });

    try {
      await userService.updateProfile({ onboardingStatus: JSON.stringify(newStatus) });
    } catch (e) {
      console.error('Failed to sync onboarding status', e);
    }
  },



  dismiss: async () => {
    const newStatus = { ...get().status, isDismissed: true };
    set({ status: newStatus, isActive: false });
    
    try {
      await userService.updateProfile({ onboardingStatus: JSON.stringify(newStatus) });
    } catch (e) {
      console.error('Failed to sync onboarding status', e);
    }
  },

  reset: async () => {
    set({ status: DEFAULT_STATUS, isActive: true, isInitialized: true, suppressedStep: null });
    try {
      await userService.updateProfile({ onboardingStatus: JSON.stringify(DEFAULT_STATUS) });
    } catch (e) {
      console.error('Failed to sync onboarding status', e);
    }
  },

  clear: () => {
    set({ status: DEFAULT_STATUS, isActive: false, isInitialized: false, suppressedStep: null });
  },
  start: () => {
    set({ isActive: true });
  },
  suppressStep: (step: OnboardingStep) => {
    set({ suppressedStep: step });
  }
}));

